/**
 * This component can be used for searching internal or external users.
 * Once user types a name of a user and hits enter, he is presented with list of users.
 * On seleting user, caller component received the date via
 * onUserSelectionChange.
 */

import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {SVGICONS} from '@ey/icons';
import {labels, currentResource} from '../../../../../../../../../../util/utils';
import {UserLookupType} from '../../../../../../../../../../util/uiconstants';
import {getUserSearch} from '../../../../../../../../../../actions/UserSearch/usersearchactionsGA';
import Loader from '../../../../../../../../LoadingIndicator/LoadingIndicator';
import ReactSelect from '../../../../../../../../../Common/SelectDropdown/SelectDropdown';
import StyledSelect from './StyledSelect';
import StyledUserSearch from './StyledUserSearch';

export default function UserSearch({
	menuPlacement,
	label,
	showValidations,
	lookupType,
	onUserClear,
	onUserSelectionChange,
	isHideClearAllIconButton,
	displayField,
	isDisabled,
	clearTextDisabled,
	value,
	showErrorMessage,
	showDropArrowOnDisabled,
	labelInternal,
	isAresErrorIcon
}) {
	const isInternal = lookupType === UserLookupType.Internal;

	const userSearcheResults = useSelector((state) => state.userData);

	const dispatch = useDispatch();

	const [selectedUser, setSelectedUser] = useState(null);
	const [defaultValue, setDefaultValue] = useState(null);
	const [userOptions, setUserOptions] = useState([]);
	const [isUserOptionsOpen, setIsUserOptionsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [startShowingResults, setStartShowingResults] = useState(false);
	const [callFailed, setCallFailed] = useState(false);
	const [failureError, setFailureError] = useState('');
	const [showValidationsState, setShowValidationsState] = useState(false);
	const [ignoreFirst, setIgnoreFirst] = useState(true);

	useEffect(() => {
		if (startShowingResults) {
			setIsUserOptionsOpen(true);
			if (userSearcheResults) {
				let users = _.map(userSearcheResults.users, getSingleUserOption);
				setUserOptions(users);
			} else setUserOptions([]);
		}
	}, [userSearcheResults]);

	//Do we need this?
	useEffect(() => {
		if (!ignoreFirst) showValidationsForSearch();
		else setIgnoreFirst(false);
	}, [showValidations]);

	const showValidationsForSearch = () => {
		if (selectedUser && selectedUser !== 'default') setShowValidationsState(false);
		else setShowValidationsState(true);
	};

	const enterKeyPress = (e) => {
		setStartShowingResults(true);
		if (e.target.value === '') {
			setUserOptions([]);
		} else {
			if (!isInternal && !validateEmail(e.target.value)) {
				setShowValidationsState(true);
				return;
			}
			getUserSearchResults(e.target.value, lookupType || 1);
		}
	};

	const formatOptionLabel = ({value, label}) => (
		<div>
			<div style={{fontWeight: 'bold'}}>{label}</div>
			<div style={{fontSize: 'small'}}>{value}</div>
		</div>
	);

	function validateEmail(emailField) {
		// eslint-disable-next-line
		var reg = /^([A-Za-z0-9_\-\.\'])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,63})$/;
		return reg.test(emailField) ? true : false;
	}

	const keyPress = (e) => {
		setShowValidationsState(false); //clear validations
		if (e.keyCode === 13 && !isUserOptionsOpen) {
			//enter press
			enterKeyPress(e);
		} else if (e.keyCode !== 38 && e.keyCode !== 40) {
			setIsUserOptionsOpen(false);
			setUserOptions([]);
		}
	};

	const clearText = () => {
		setCallFailed(false);
		setFailureError('');
		setDefaultValue(null);
		setIsUserOptionsOpen(false);
		if (onUserClear) {
			onUserClear();
		}
	};

	const getSingleUserOption = (user) => {
		return {
			label: user.displayName,
			value: user.email,
			allProps: user
		};
	};

	const getUserSearchResults = (searchKey, userLookUpType) => {
		setCallFailed(false);
		setFailureError('');
		setIsLoading(true);
		dispatch(getUserSearch(searchKey, userLookUpType))
			.then((result) => {
				setIsLoading(false);
				setUserSearchFocus();
			})
			.catch((e) => {
				setFailureError('Failure:' + e);
				setCallFailed(true);
				setIsLoading(false);
				setUserOptions([]);
			});
	};

	const setUserSearchFocus = () => {
		let ddl = document.querySelector('#usesearch');
		ddl.querySelector('.react-select__input').children[0].focus();
	};

	const displayLabel = isInternal ? labelInternal : labels.emailForExternal;

	const renderSelect = () => {
		const searchDisabled = (value !== null && value !== undefined) || isDisabled;
		//We need to show the name on the input.
		const name = value && {label: '', value: label, allProps: []};

		const onChange = (option) => {
			if (option) {
				const opt = {label: '', value: option.value, allProps: option.allProps};
				setIsUserOptionsOpen(false);
				setDefaultValue(opt);
				setUserOptions([]);
				if (onUserSelectionChange) {
					onUserSelectionChange(opt);
				}
			} else {
				setDefaultValue('');
				setSearchDisabled(false);
			}
		};

		const props = {
			className: classNames('select-group', 'pull-left', {inputDisabled: searchDisabled}),
			defaultValue,
			errorMessage: isInternal ? currentResource.groupStructure.required : labels.inValidEmail,
			formLabel: displayLabel,
			formatOptionLabel: formatOptionLabel,
			id: 'usesearch',
			isClearable: false,
			isDisabled: searchDisabled,
			isSearchable: true,
			menuIsOpen: isUserOptionsOpen,
			menuPlacement,
			noOptionsMessage: () => labels.noRecord,
			onBlur: () => setIsUserOptionsOpen(false),
			onChange,
			onInputKeyDown: keyPress,
			onLabelClickSetFocus: true,
			options: userOptions,
			searchInLabelOrValue: true,
			showErrorMessage: showValidationsState || showErrorMessage,
			value: name,
			showDropArrowOnDisabled,
			isAresErrorIcon
		};

		return <StyledSelect {...props} />;
	};

	const renderClearButton = () => {
		const props = {
			styleName: 'medium',
			name: 'close',
			hoverText: labels.clearAllValues,
			alt: 'Clear'
		};

		const wrpProps = {
			title: labels.clearAllValues,
			className: classNames('pull-left', 'clearall', {disabled: isDisabled || clearTextDisabled}),
			onClick: () => {
				clearText();
			}
		};

		return (
			<span {...wrpProps}>
				<SVGICONS {...props} />
			</span>
		);
	};

	return (
		<StyledUserSearch hasError={!!showErrorMessage} className="userfield-row nameandemail">
			{callFailed && <div className="error"> {failureError} </div>}
			<div className="user-selectbox">
				{renderSelect()}
				{!isHideClearAllIconButton && renderClearButton()}
				<div className="enterdetail-loading">
					{isLoading && <Loader show view="inlineView" styleName="nameemailloader" />}
				</div>
			</div>
			<div className="enterdetail-description">
				{isInternal ? labels.userSearchPlaceholder : labels.userSearchPlaceholderForExternal}
			</div>
		</StyledUserSearch>
	);
}

UserSearch.propTypes = {
	label: PropTypes.string,
	showValidations: PropTypes.bool,
	menuPlacement: PropTypes.oneOf(['bottom', 'auto', 'top']),
	displayField: PropTypes.string,
	isDisabled: PropTypes.bool,
	clearTextDisabled: PropTypes.bool,
	value: PropTypes.arrayOf(PropTypes.shape({value: PropTypes.string, label: PropTypes.string})).isRequired
};

UserSearch.defaultProps = {
	label: null,
	showValidations: false,
	menuPlacement: 'auto',
	displayField: null,
	isDisabled: false,
	clearTextDisabled: false
};
